import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGoogleMapsApi } from '../../services/googleMapsLoader';
import { debounce } from '../../utils/debounce';
import './Combobox.css';

/**
 * PlacesAutocomplete component for location search using Google Maps JavaScript API
 * 
 * @param {Object} props
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.value - Selected value
 * @param {string} props.id - ID for the input element
 * @param {boolean} props.required - Whether the field is required
 */
const PlacesAutocomplete = ({
  onChange,
  placeholder = 'Search for a location',
  value = '',
  id,
  required = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const sessionTokenRef = useRef(null);

  // Use our centralized Google Maps loader
  const { isLoaded, error: loadError } = useGoogleMapsApi();

  // Initialize input value from props
  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  // Initialize session token for Google Places API
  useEffect(() => {
    if (!isLoaded || loadError) return;
    
    // Create a new session token
    if (window.google.maps.places.AutocompleteSessionToken) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    
    return () => {
      // Clear session token when component unmounts
      sessionTokenRef.current = null;
    };
  }, [isLoaded, loadError]);
  
  // Prevent form submission on enter
  useEffect(() => {
    const currentInput = inputRef.current;
    if (!currentInput) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();

        // If there's a single prediction, select it
        if (predictions.length === 1) {
          handlePredictionClick(predictions[0]);
        }
      }
    };

    currentInput.addEventListener('keydown', handleKeyDown);

    return () => {
      currentInput.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predictions]);

  // Create a debounced function for fetching predictions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPredictions = useCallback(
    debounce((input) => {
      if (!isLoaded || !input || input.length < 3) {
        setPredictions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      
      const autocomplete = new window.google.maps.places.AutocompleteService();
      const request = {
        input,
        componentRestrictions: { country: 'us' },
        types: ['geocode'],
        sessionToken: sessionTokenRef.current
      };
      
      autocomplete.getPlacePredictions(request, (results, status) => {
        setIsSearching(false);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results) {
          console.log('No predictions found, status:', status);
          setPredictions([]);
          return;
        }
        console.log('Predictions received:', results.length);
        setPredictions(results.map(prediction => ({
          ...prediction,
          description: prediction.description || ''
        })));
      });
    }, 500), // 500ms debounce delay
    [isLoaded]
  );
  
  // Function to handle place selection
  const handlePlaceSelect = useCallback(async (placeId) => {
    if (!isLoaded || !placeId) return;
    
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    
    try {
      const place = await new Promise((resolve, reject) => {
        service.getDetails({
          placeId,
          fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
          sessionToken: sessionTokenRef.current
        }, (place, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
            reject(new Error(`Error fetching place details: ${status}`));
            return;
          }
          resolve(place);
        });
      });
      
      // Create a new session token for the next search
      if (window.google.maps.places.AutocompleteSessionToken) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
      
      console.log('Place selected:', place);
      
      // Extract address components
      const addressComponents = {};
      for (const component of place.address_components || []) {
        const type = component.types[0];
        addressComponents[type] = component.long_name;
      }
      
      // Create formatted address if not provided
      let formattedAddress = place.formatted_address;
      if (!formattedAddress) {
        const streetNumber = addressComponents.street_number || '';
        const route = addressComponents.route || '';
        const locality = addressComponents.locality || '';
        const state = addressComponents.administrative_area_level_1 || '';
        const zip = addressComponents.postal_code || '';
        formattedAddress = `${streetNumber} ${route}, ${locality}, ${state} ${zip}`.trim();
      }
      
      const locationData = {
        placeId: place.place_id,
        address: formattedAddress,
        name: place.name || formattedAddress,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        addressComponents: addressComponents
      };
      
      setInputValue(formattedAddress);
      setPredictions([]);
      onChange(formattedAddress, locationData);
    } catch (error) {
      console.error('Error in handlePlaceSelect:', error);
    }
  }, [isLoaded, onChange]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    fetchPredictions(value);
  };
  
  // Handle prediction selection
  const handlePredictionClick = (prediction) => {
    handlePlaceSelect(prediction.place_id);
  };

  if (loadError) {
    return <div className="text-danger">Error loading Google Maps API: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div className="text-muted">Loading Places API...</div>;
  }

  return (
    <div className="combobox-container" ref={wrapperRef}>
      <div className="combobox-input-wrapper">
        <input
          id={id}
          ref={inputRef}
          type="text"
          className="combobox-input"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
        />
        <span className="combobox-arrow"></span>
      </div>
      
      {isSearching && (
        <div className="combobox-dropdown">
          <div className="combobox-loading">Loading...</div>
        </div>
      )}
      
      {!isSearching && predictions.length > 0 && (
        <div className="combobox-dropdown">
          {predictions.map((prediction) => (
            <div 
              key={prediction.place_id}
              className="combobox-option"
              onClick={() => handlePredictionClick(prediction)}
            >
              {prediction.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
